import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useStory } from '../../context/StoryContext';
import { generateImage } from '../../services/aiService';
import { useAuth } from '../../context/AuthContext';
import * as dbService from '../../services/dbService';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../lib/supabaseClient';
import * as FiIcons from 'react-icons/fi';

const CharacterSetup = () => {
  const navigate = useNavigate();
  const { state, updateCharacter } = useStory();
  const { user } = useAuth();
  const { character } = state;
  const [isCustomGender, setIsCustomGender] = React.useState(
    character.gender && !['Male', 'Female', 'Non-binary', ''].includes(character.gender)
  );

  const [savedCharacters, setSavedCharacters] = useState([]);
  const [characterStats, setCharacterStats] = useState([]);
  const [selectedCharacterId, setSelectedCharacterId] = useState('');
  const [isCreatingNew, setIsCreatingNew] = useState(true);
  const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);

  useEffect(() => {
    if (state.supabaseStoryId && state.ownerId && user && state.ownerId !== user.id) {
      alert("Only owners can modify the story setup.");
      navigate('/story');
    }
  }, [state.supabaseStoryId, state.ownerId, user, navigate]);

  useEffect(() => {
    const fetchCharactersAndStats = async () => {
      if (user) {
        try {
          const [chars, stats, userStories, { data: collabs }] = await Promise.all([
            dbService.getCharacters(user.id),
            dbService.getCharacterStoriesInfo(),
            dbService.getUserStories(user.id),
            supabase.from('collaborators').select('character_id, stories(world_genre)').eq('status', 'Accepted')
          ]);

          const safeCollabs = collabs || [];
          const safeStories = userStories || [];

          const augmentedStats = (chars || []).map(char => {
             const rpcStat = (stats || []).find(s => s.character_id === char.id || s.character_name === char.name) || { active_count: 0, genres: '' };
             const ownedStories = safeStories.filter(s => s.character_name === char.name);
             const collabStories = safeCollabs.filter(c => c.character_id === char.id);

             const totalActive = Math.max(Number(rpcStat.active_count || 0), ownedStories.length + collabStories.length);
             return { character_id: char.id, character_name: char.name, active_count: totalActive };
          });

          setSavedCharacters(chars || []);
          setCharacterStats(augmentedStats);
          if (chars && chars.length > 0) {
            setIsCreatingNew(false);
            setSelectedCharacterId(chars[0].id);
            updateCharacter(chars[0]);
          }
        } catch (err) {
          console.error("Failed to fetch characters or stats", err);
        }
      }
    };
    fetchCharactersAndStats();
  }, [user]);

  const isTierRestricted = state.subscriptionTier !== 'Storyteller' && state.subscriptionTier !== 'Worldbuilder';


  const handleGenerateAvatar = async () => {
    if (!character.appearance) {
      alert("Please provide appearance details first.");
      return;
    }
    setIsGeneratingAvatar(true);
    try {
      const url = await generateImage(
        "A character portrait.",
        character,
        "realistic",
        []
      );
      updateCharacter({ avatar: url });
    } catch (e) {
      console.error(e);
      alert("Failed to generate avatar.");
    } finally {
      setIsGeneratingAvatar(false);
    }
  };

  const handleGenderChange = (e) => {
    const val = e.target.value;
    if (val === 'Other') {
      setIsCustomGender(true);
      updateCharacter({ gender: '' });
    } else {
      setIsCustomGender(false);
      updateCharacter({ gender: val });
    }
  };

  const handleSelectCharacter = (e) => {
    const id = e.target.value;
    if (id === 'new') {
      setIsCreatingNew(true);
      setSelectedCharacterId('');
      updateCharacter({
        name: '', nickname: '', preferredReference: 'name', role: '', gender: '', appearance: '', backstory: '', avatar: '', traits: [], equipment: [], stats: { level: 1, health: 100, mana: 50, strength: 10, agility: 10, intelligence: 10, experience: 0 }
      });
    } else {
      setIsCreatingNew(false);
      setSelectedCharacterId(id);
      const selected = savedCharacters.find(c => c.id === id);
      if (selected) {
        updateCharacter({
          ...selected,
          preferredReference: selected.preferred_reference || 'name'
        });
        setIsCustomGender(selected.gender && !['Male', 'Female', 'Non-binary', ''].includes(selected.gender));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCreatingNew && user) {
      try {
        const newChar = await dbService.createCharacter(user.id, {
          name: character.name,
          nickname: character.nickname,
          preferred_reference: character.preferredReference || 'name',
          role: character.role,
          gender: character.gender,
          appearance: character.appearance,
          backstory: character.backstory,
          avatar: character.avatar,
          traits: typeof character.traits === 'string' ? character.traits.split(',').map(t => t.trim()).filter(Boolean) : (character.traits || []),
          equipment: typeof character.equipment === 'string' ? character.equipment.split(',').map(t => t.trim()).filter(Boolean) : (character.equipment || []),
          stats: character.stats || { level: 1, health: 100, mana: 50, strength: 10, agility: 10, intelligence: 10, experience: 0 }
        });
        updateCharacter(newChar);
      } catch (err) {
        console.error("Failed to save new character", err);
      }
    }
    navigate('/setup/ai');
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
        <div className="mb-8">
          <p className="text-amber-500 text-sm font-medium tracking-wider uppercase mb-2">Step 2 of 3</p>
          <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-gray-100">Forge Your Avatar</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Who will you be in this world?</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-gray-400/50 dark:bg-gray-900/50 p-8 rounded-2xl border border-gray-400 dark:border-gray-800">
          
          {user && savedCharacters.length > 0 && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-purple-900/20 rounded-xl border border-gray-200 dark:border-purple-800">
              <label className="label-text mb-2 block">Choose an existing character or create a new one:</label>
              <select
                value={isCreatingNew ? 'new' : selectedCharacterId}
                onChange={handleSelectCharacter}
                className="input-field"
              >
                <option value="new">+ Create New Character</option>
                {savedCharacters.map(char => {
                  const stat = characterStats.find(s => s.character_id === char.id || s.character_name === char.name);
                  const activeCount = stat ? Number(stat.active_count) : 0;
                  const disabled = isTierRestricted && activeCount > 0;
                  return (
                    <option key={char.id} value={char.id} disabled={disabled}>
                      {char.name} ({char.role}) {disabled ? '- Active in another story (Upgrade required)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <label className="label-text">Character Name</label>
              <input 
                type="text"
                value={character.name || ''}
                onChange={(e) => updateCharacter({ name: e.target.value })}
                className="input-field"
                placeholder="e.g., Elara Vance"
                required
                disabled={!isCreatingNew}
              />
            </div>
            <div className="md:col-span-1">
              <label className="label-text">Nickname</label>
              <input
                type="text"
                value={character.nickname || ''}
                onChange={(e) => updateCharacter({ nickname: e.target.value })}
                className="input-field"
                placeholder="e.g., Ellie"
                disabled={!isCreatingNew}
              />
            </div>
            <div className="md:col-span-1">
              <label className="label-text">Role / Class / Title</label>
              <input 
                type="text"
                value={character.role || ''}
                onChange={(e) => updateCharacter({ role: e.target.value })}
                className="input-field"
                placeholder="e.g., Rogue Scholar"
                required
                disabled={!isCreatingNew}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label-text">Preferred Story Reference</label>
              <p className="text-xs text-gray-800/70 dark:text-purple-300/60 mb-2">How should the narrative refer to you in passages?</p>
              <div className="flex gap-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="preferredReference"
                    value="name"
                    checked={character.preferredReference !== 'nickname'}
                    onChange={(e) => updateCharacter({ preferredReference: e.target.value })}
                    className="form-radio text-gray-800 focus:ring-purple-500"
                    disabled={!isCreatingNew}
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-purple-200">Full Name</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="preferredReference"
                    value="nickname"
                    checked={character.preferredReference === 'nickname'}
                    onChange={(e) => updateCharacter({ preferredReference: e.target.value })}
                    className="form-radio text-gray-800 focus:ring-purple-500"
                    disabled={!isCreatingNew}
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-purple-200">Nickname</span>
                </label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label-text">Gender</label>
              <select
                value={isCustomGender ? 'Other' : character.gender || ''}
                onChange={handleGenderChange}
                className="input-field"
                required
                disabled={!isCreatingNew}
              >
                <option value="" disabled>Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Non-binary">Non-binary</option>
                <option value="Other">Other</option>
              </select>
            </div>
            {isCustomGender && (
              <div>
                <label className="label-text">Custom Gender</label>
                <input
                  type="text"
                  value={character.gender || ''}
                  onChange={(e) => updateCharacter({ gender: e.target.value })}
                  className="input-field"
                  placeholder="Specify gender"
                  required
                  disabled={!isCreatingNew}
                />
              </div>
            )}
          </div>

          <div>
            <label className="label-text">Appearance Details</label>
            <textarea 
              value={character.appearance || ''}
              onChange={(e) => updateCharacter({ appearance: e.target.value })}
              className="input-field min-h-[100px]"
              placeholder="e.g., Tall, piercing green eyes, always wears a tattered leather coat."
              required
              disabled={!isCreatingNew}
            />
          </div>

          <div className="md:col-span-2">
             <label className="label-text">Avatar URL (Optional)</label>
             <div className="flex gap-2">
               <input
                 type="text"
                 value={character.avatar || ''}
                 onChange={(e) => updateCharacter({ avatar: e.target.value })}
                 className="input-field flex-1"
                 placeholder="https://example.com/avatar.png"
                 disabled={!isCreatingNew}
               />
               {!isTierRestricted && (
                 <button
                   type="button"
                   onClick={handleGenerateAvatar}
                   disabled={isGeneratingAvatar || !isCreatingNew}
                   className="btn-secondary whitespace-nowrap flex items-center space-x-2"
                 >
                   <SafeIcon icon={isGeneratingAvatar ? FiIcons.FiLoader : FiIcons.FiImage} className={isGeneratingAvatar ? 'animate-spin' : ''} />
                   <span>{isGeneratingAvatar ? 'Generating...' : 'Visualize'}</span>
                 </button>
               )}
             </div>
             {character.avatar && (
               <div className="mt-4 w-24 h-24 rounded-full overflow-hidden border-2 border-gray-200 dark:border-purple-800">
                 <img src={character.avatar} alt="Avatar Preview" className="w-full h-full object-cover" />
               </div>
             )}
          </div>


          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label-text">Traits (comma separated)</label>
              <input
                type="text"
                value={typeof character.traits === 'string' ? character.traits : (character.traits || []).join(', ')}
                onChange={(e) => updateCharacter({ traits: e.target.value })}
                className="input-field"
                placeholder="e.g. Brave, Quick-witted"
                disabled={!isCreatingNew}
              />
            </div>
            <div>
              <label className="label-text">Equipment (comma separated)</label>
              <input
                type="text"
                value={typeof character.equipment === 'string' ? character.equipment : (character.equipment || []).join(', ')}
                onChange={(e) => updateCharacter({ equipment: e.target.value })}
                className="input-field"
                placeholder="e.g. Sword, Shield"
                disabled={!isCreatingNew}
              />
            </div>
          </div>

          <div className="pt-4 border-t border-gray-300 dark:border-gray-700">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">RPG Stats</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['level', 'health', 'mana', 'strength', 'agility', 'intelligence', 'experience'].map(stat => (
                <div key={stat}>
                  <label className="label-text capitalize">{stat}</label>
                  <input
                    type="number"
                    value={character.stats?.[stat] || 0}
                    onChange={(e) => updateCharacter({ stats: { ...character.stats, [stat]: parseInt(e.target.value) || 0 } })}
                    className="input-field"
                    disabled={!isCreatingNew}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="label-text">Backstory & Motivations</label>
            <textarea 
              value={character.backstory || ''}
              onChange={(e) => updateCharacter({ backstory: e.target.value })}
              className="input-field min-h-[120px]"
              placeholder="What drives your character? What are they running from, or running towards?"
              required
              disabled={!isCreatingNew}
            />
          </div>

          <div className="flex justify-between pt-4 border-t border-gray-800">
            <button 
              type="button" 
              onClick={() => navigate('/setup/world')}
              className="btn-secondary flex items-center space-x-2"
            >
              <SafeIcon icon={FiIcons.FiChevronLeft} />
              <span>Back</span>
            </button>
            <button type="submit" className="btn-primary flex items-center space-x-2">
              <span>Next: Configure AI</span>
              <SafeIcon icon={FiIcons.FiChevronRight} />
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CharacterSetup;
